/**
 * The signed-in user's role in their currently-active org.
 *
 * Reads from `claims.orgs[primaryOrgId]` — the same Firebase Auth
 * custom-claim map that drives Firestore rules. Synchronous after
 * the first hydration of `useTokenClaims`; no extra Firestore round-
 * trip.
 *
 * Returns `null` while claims are loading, or when the user has no
 * primary org / isn't a member of any. Callers MUST treat `null`
 * conservatively (no privileged UI).
 *
 * Mirrors mobile's `usePermissions().role` resolution. UI uses this
 * for role-driven affordances:
 *   - Hide "Billing" + "Plans" nav items unless `superAdmin`
 *   - Hide "Delete project" unless `superAdmin | admin`
 *   - Hide "Add transaction" unless `canFinance(role)` (SA / Admin
 *     / Manager / Accountant — see `@shared/perms`)
 *
 * Capability layering: for more granular gates (e.g. project.delete
 * vs project.edit) we can layer a small `can()` helper on top later
 * — for now the surfaces we care about gate cleanly on role alone.
 */
import { useMemo } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import { useFullOrganization } from '@/lib/useFullOrganization';
import { useTokenClaims } from '@/lib/useTokenClaims';

// OrgRole used to be a local re-declaration of the 9 org roles.
// Now it's a type alias for the canonical `RoleKey` from
// `@shared/perms` so mobile + web + functions all read one
// definition. The `OrgRole` alias stays so existing call sites
// keep working without a churn-y rename.
import type { RoleKey } from '@shared/perms';
export type OrgRole = RoleKey;

export type UseCurrentOrgRoleResult = {
  /** Role in the active org. `null` while loading or when the user
   *  has no primary org. */
  role: OrgRole | null;
  loading: boolean;
};

const VALID_ROLES: ReadonlySet<string> = new Set<OrgRole>([
  'superAdmin',
  'admin',
  'manager',
  'accountant',
  'designer',
  'siteEngineer',
  'supervisor',
  'viewer',
  'client',
]);

export function useCurrentOrgRole(): UseCurrentOrgRoleResult {
  const { claims, loading } = useTokenClaims();
  const { user } = useAuth();
  const { org } = useFullOrganization();
  const role = useMemo<OrgRole | null>(() => {
    // OWNER FIRST: if the live org doc says the signed-in user IS the
    // owner, they are superAdmin regardless of what the (possibly
    // stale/corrupted) token claims say — a bug once wrote
    // roles[ownerId]='client' and the claims mirrored it, demoting the
    // owner across the whole web app. Guarded on claims.primaryOrgId
    // matching the org doc so a mid-org-switch snapshot lag can't leak
    // ownership of the previous org into the next one.
    if (
      user &&
      org &&
      org.data.ownerId === user.uid &&
      (!claims.primaryOrgId || claims.primaryOrgId === org.id)
    ) {
      return 'superAdmin';
    }
    // Paused seat (studio over its plan's member cap after the grace
    // period) → no role, even while the token claims are still stale.
    const paused = org?.data.pausedMemberIds;
    if (
      user &&
      org &&
      (!claims.primaryOrgId || claims.primaryOrgId === org.id) &&
      Array.isArray(paused) &&
      paused.includes(user.uid)
    ) {
      return null;
    }
    const pid = claims.primaryOrgId;
    if (!pid) return null;
    const r = claims.orgs[pid];
    return r && VALID_ROLES.has(r) ? (r as OrgRole) : null;
  }, [claims, user, org]);
  return { role, loading };
}

/** Convenience: true when the active role is in the allowed list.
 *  False when role is null (loading or no org) — fail-closed. */
export function isRoleOneOf(
  role: OrgRole | null,
  allowed: readonly OrgRole[],
): boolean {
  if (!role) return false;
  return allowed.includes(role);
}
